import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useServices } from '../ServiceContext'
import type { LayoutInput, ListLayoutsParams } from '../types'

// ─── Query keys ───────────────────────────────────────────────────────────────

export const layoutKeys = {
  all: ['layouts'] as const,
  list: (params?: ListLayoutsParams) => ['layouts', 'list', params] as const,
  detail: (id: string) => ['layouts', 'detail', id] as const,
}

// ─── Queries ──────────────────────────────────────────────────────────────────

export function useLayouts(params?: ListLayoutsParams) {
  const { layoutService } = useServices()
  return useQuery({
    queryKey: layoutKeys.list(params),
    queryFn: () => layoutService.list(params),
  })
}

export function useLayout(id: string) {
  const { layoutService } = useServices()
  return useQuery({
    queryKey: layoutKeys.detail(id),
    queryFn: () => layoutService.get(id),
    enabled: Boolean(id),
  })
}

// ─── Mutations ────────────────────────────────────────────────────────────────

export function useCreateLayout() {
  const { layoutService } = useServices()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: LayoutInput) => layoutService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: layoutKeys.all })
    },
  })
}

export function useUpdateLayout(id: string) {
  const { layoutService } = useServices()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: LayoutInput) => layoutService.update(id, input),
    onSuccess: (updated) => {
      queryClient.setQueryData(layoutKeys.detail(id), updated)
      queryClient.invalidateQueries({ queryKey: layoutKeys.all })
    },
  })
}

export function useCloneLayout() {
  const { layoutService } = useServices()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name?: string }) => layoutService.clone(id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: layoutKeys.all })
    },
  })
}

export function useDeleteLayout() {
  const { layoutService } = useServices()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => layoutService.delete(id),
    onSuccess: (_data, id) => {
      queryClient.removeQueries({ queryKey: layoutKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: layoutKeys.all })
    },
  })
}
